define([
    'underscore',
], (_) => {

    return () => ({
        getAllCoverages,
        getAllSelectedCoverages,
        getCoverageFromMfpLineByCodeAndGroup,
        getAllCoverageCodesFromOfferings,
        getCoverageStructureFromOfferings,
        getCoveragesGroupedByCoverable,
        getCoveragesGroupedForDisplay,
        getGroupedPromotionsForDisplay,
        hasPromotionsInAnyGroup,
        getPromotionSumFromGroupedPromotions,
    });

    function hasPromotionsInAnyGroup(groupedPromotions) {
        return groupedPromotions.some(group => group.promotions.length > 0);
    }

    function addSummedPromotionGroupCost(summedCost, promotionGroup) {
        return promotionGroup.promotions.reduce(
            (summedPromotionsCost, {cost}) => ({
                // Multiply by 100 to prevent rounding bugs
                amount: (summedPromotionsCost.amount * 100 + cost.amount * 100) / 100,
                currency: cost.currency,
            }),
            summedCost
        );
    }

    function getPromotionSumFromGroupedPromotions(groupedPromotions) {
        return groupedPromotions.reduce(
            (summedCost, promotionGroup) => addSummedPromotionGroupCost(summedCost, promotionGroup),
            {amount: 0}
        );
    }

    /**
     * Maps the list received from {@link getCoveragesGroupdForDisplay} to include only promotion information (freeCostElements).
     *
     * @param {Object} The mFPLine found under one offering -> lobs
     * @returns {Array}
     */
    function getGroupedPromotionsForDisplay(mfpLine) {
        return getCoveragesGroupedForDisplay(mfpLine).map(group => ({
            title: group.title,
            promotions: _.flatten(
                group.coverages
                    .map(coverage =>
                        coverage.freeCostElements && coverage.freeCostElements.map(promotion =>
                            Object.assign({coverageName: coverage.name}, promotion)
                        ))
                    .filter(promotion => promotion)
            ),
        }));
    }

    /**
     * Returns a list of all coverages sorted by priority
     * by getting them from line coverage and vehicle coverages from both vehicles.
     * @param mfpLine
     * @returns {Array}
     */
    function getAllCoverages(mfpLine) {
        const lineCoverages = mfpLine.lineCoverages || [];
        const vehicleCoverages = getAllVehicleCoverages(mfpLine);
        const allCoverages = lineCoverages.concat(vehicleCoverages);
        return _.sortBy(allCoverages, 'priority');
    }

    /**
     * Returns all coverages in a way that's easy to display on the UI.
     *
     * The result has the following structure:
     * [{ title: 'MFPLine', coverages: [list of line coverages] },
     *  { title: 'vehicle1', coverages: [list of vehicle coverages] },
     *  ...]
     *
     * However, it will merge all groups into one 'all' group if there's only one vehicle, since we group
     * them all together on the UI for simplicity in the case of single vehicle policies:
     * [{ title: 'all', coverages: [list of all coverages for that mfp line]}]
     *
     * All coverages are sorted by priority.
     * The list starts with line coverages, then attaches vehicle coverages in their order on the mfpLine object.
     *
     * @param mfpLine
     * @param removeTitles
     * @returns {Array}
     */
    function getCoveragesGroupedForDisplay(mfpLine, removeTitles) {
        function filterTitle(coverableName) {
            return _.contains(removeTitles, coverableName) ? undefined : coverableName;
        }

        const titleMapper = removeTitles ? filterTitle : _.identity;

        if (mfpLine.vehicleCoverages.length < 2) {
            return [{title: titleMapper('all'), coverages: getAllCoverages(mfpLine)}];
        }

        return _.pairs(getCoveragesGroupedByCoverable(mfpLine))
            .map(pair => ({
                title: titleMapper(pair[0]),
                coverages: pair[1],
            }));
    }

    /**
     * Returns all coverages grouped by coverable as a map.
     *
     * Example:
     * {
     *  'mFPLine': [line coverages],
     *  'vehicle 1': [coverages for vehicle 1],
     *  'vehicle 2': [coverages for vehicle 2],
     * }
     * @param mfpLine
     * @returns {Object}
     */
    function getCoveragesGroupedByCoverable(mfpLine) {
        return _.extend({mFPLine: _.sortBy(mfpLine.lineCoverages, 'priority')}, getVehicleCoveragesGroupedByVehicle(mfpLine));
    }

    function getVehicleCoveragesGroupedByVehicle(mfpLine) {
        if (!mfpLine.vehicleCoverages || mfpLine.vehicleCoverages.length === 0) {
            return {};
        }
        const nameCoveragesPairs = _.sortBy(mfpLine.vehicleCoverages, 'vehicleNumber')
            .map(vehicleCoverage => [vehicleCoverage.vehicleName, //item0
                _.sortBy(vehicleCoverage.coverages, 'priority'), //item1
                vehicleCoverage.vehicleNumber]); //item2

        const uniqVehicleNames = _.uniq(_.pluck(mfpLine.vehicleCoverages, 'vehicleName'));
        if (mfpLine.vehicleCoverages.length > uniqVehicleNames.length) {
            //there are duplicates with the same vehicleName, so need to adjust vehicle name to avoid unnecessary grouping: "vehicleName (vehicleNumber)"
            _.each(nameCoveragesPairs, (vehicleNameCovNumber) => {
                vehicleNameCovNumber[0] = `${vehicleNameCovNumber[0]} (${vehicleNameCovNumber[2]})`;
            });
        }

        //create object from of item[0]: item[1] (ignoring item[2])
        return _.object(nameCoveragesPairs);
    }

    /**
     * Returns an ordered list of all selected coverages on the mfp line and vehicles.
     * @param mfpLine
     * @returns {*}
     */
    function getAllSelectedCoverages(mfpLine) {
        return _.where(getAllCoverages(mfpLine), {selected: true});
    }

    /**
     * Finds a coverage in an offering by coverageCode as returned by getCoveragesGroupedForDisplay and coverable.
     * @param mfpLine
     * @param coverageCode
     * @param group
     * @returns {*}
     */
    function getCoverageFromMfpLineByCodeAndGroup(mfpLine, coverageCode, group) {
        const coverageGroup = _.find(getCoveragesGroupedForDisplay(mfpLine),
            elt => elt.title.toLowerCase() === group.toLowerCase());
        return coverageGroup ?
            _.findWhere(coverageGroup.coverages, {patternCode: coverageCode}) :
            undefined;
    }

    /**
     * Gets all available coverage codes from a list of offerings
     * @param offerings
     */
    function getAllCoverageCodesFromOfferings(offerings) {
        return _.chain(offerings)
            .map(offering => getAllCoverages(offering.lobs.mFPLine))
            .flatten()
            .sortBy('priority')
            .pluck('patternCode')
            .uniq()
            .value();
    }

    function getCoverageStructureFromOfferings(offerings) {

        /* merge by adding group2 to group1, modifies group1! */
        function mergeCoverageGroups(group1, group2) {
            group2.forEach((element) => {
                const correspondingInGroup1 = _.findWhere(group1, {title: element.title});
                if (correspondingInGroup1) {
                    const allCoverages = correspondingInGroup1.coverages.concat(element.coverages);
                    const uniqueCoverages = _.uniq(allCoverages, coverage => coverage.patternCode);
                    const sortedCoverages = _.sortBy(uniqueCoverages, 'priority');
                    correspondingInGroup1.coverages = sortedCoverages;
                } else {
                    throw new Error();
                }
            });
            return group1;
        }

        return offerings
            .map(offering => getCoveragesGroupedForDisplay(offering.lobs.mFPLine))
            .reduce(mergeCoverageGroups)
            .map(coverageGroup => ({
                title: coverageGroup.title,
                coverageCodes: _.pluck(coverageGroup.coverages, 'patternCode'),
            }));
    }

    /**
     * Returns a list of all vehicle coverages
     * @param mfpLine
     * @returns {*}
     */
    function getAllVehicleCoverages(mfpLine) {
        if (!mfpLine.vehicleCoverages || mfpLine.vehicleCoverages.length === 0) {
            return [];
        }
        if (mfpLine.vehicleCoverages.length === 1) {
            return mfpLine.vehicleCoverages[0].coverages.slice(); // return a copy of coverages, not the array itself
        }
        return mfpLine.vehicleCoverages.reduce((vehicle1, vehicle2) => vehicle1.coverages.concat(vehicle2.coverages));
    }

});



// WEBPACK FOOTER //
// ../common/js/src/edge/quoteandbind/mfp/services/MFPCoverageService.js